import React, { useCallback, useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import List from "./List";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaymentTransactions, fetchPaymentTransactionStats } from "../../../redux/slice/subscriptionSlice";
import { useLoader } from "../../../context/LoaderContext";
import { PaymentIcon } from "../../../assets/icons";
import { TabCard } from "../../../components/custom-card/Cards";
import { CheckCircle, Clock, AlertCircle } from "lucide-react";

const PaymentList = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();

  // Pagination
  const [limit, setLimit] = useState({ value: 10, label: 10 });
  const [currentPage, setCurrentPage] = useState(1);

  // Search Bar
  const [searchTerm, setSearchTerm] = useState("");

  // Status Filter
  const [selectedStatus, setSelectedStatus] = useState("all");

  const { paymentTransactions = [], totalPaymentTransactions = 0, paymentTransactionStats = null } = useSelector(
    (state) => state?.subscription || {}
  );

  const searchTimeout = useRef(null);

  // Debounced Search/Fetch Handler
  const getPaymentsData = useCallback(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    setLoading(true);
    searchTimeout.current = setTimeout(() => {
      Promise.all([
        dispatch(fetchPaymentTransactionStats()).unwrap(),
        dispatch(
          fetchPaymentTransactions({
            page: currentPage,
            limit: limit?.value,
            search: searchTerm,
            status: selectedStatus === "all" ? "" : selectedStatus,
          })
        ).unwrap(),
      ]).finally(() => {
        setLoading(false);
      });
    }, 500);
  }, [currentPage, limit?.value, searchTerm, selectedStatus, dispatch, setLoading]);

  // Trigger API call when search, page, limit, or status changes
  useEffect(() => {
    getPaymentsData();
    return () => clearTimeout(searchTimeout.current);
  }, [getPaymentsData]);

  const AllTypesOfUserCard = [
    {
      id: 1,
      total: paymentTransactionStats?.total ?? 0,
      title: "Total Transactions",
      icon: <PaymentIcon />,
      iconClassName: "icon-primary",
      statusValue: "all",
    },
    {
      id: 2,
      total: paymentTransactionStats?.paid ?? 0,
      title: "Paid Transactions",
      icon: <CheckCircle size={24} />,
      iconClassName: "icon-success",
      statusValue: "paid",
    },
    {
      id: 3,
      total: paymentTransactionStats?.pending ?? 0,
      title: "Pending Transactions",
      icon: <Clock size={24} />,
      iconClassName: "icon-warning",
      statusValue: "pending",
    },
    {
      id: 4,
      total: paymentTransactionStats?.failed ?? 0,
      title: "Failed Transactions",
      icon: <AlertCircle size={24} />,
      iconClassName: "icon-danger",
      statusValue: "failed",
    },
  ];

  return (
    <>
      <Container fluid className="px-0">
        {/* Card Section */}
        <div className="grid gap-4">
          {AllTypesOfUserCard?.map((item, i) => (
            <TabCard
              key={i}
              count={item?.total}
              title={item?.title}
              className={`g-col-4 g-col-xxl-3 ${!item.statusValue ? "pe-none" : "cursor-pointer"}`}
              icon={item?.icon}
              active={selectedStatus === item.statusValue}
              iconClassName={item.iconClassName || "icon-secondary"}
              onClick={() => {
                if (item.statusValue) {
                  setSelectedStatus(item.statusValue);
                  setCurrentPage(1);
                }
              }}
            />
          ))}
        </div>

        <div className="bg-white rounded-2 overflow-hidden common-shadow mt-4">
          {/* List Section (CustomTable with built-in pagination) */}
          <List
            data={paymentTransactions}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            limit={limit?.value}
            setLimit={setLimit}
            total={totalPaymentTransactions}
          />
        </div>
      </Container>
    </>
  );
};

export default PaymentList;
